import { Flex, FlexProps, Spinner } from "@chakra-ui/react";
import { Table } from "@tanstack/react-table";
import omit from "lodash/omit";
import { ReactNode } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { DataTableCustomizer } from "@/common/components/DataTable/DataTableCustomizer";
import DataTablePagination from "@/common/components/DataTable/DataTablePagination";
import {
  DataTableHasCustomization,
  DataTableHasPagination,
  DataTableHasSearch,
} from "@/common/components/DataTable/DataTableTypes";
import SearchInput from "@/common/components/SearchInput";
import {
  getId,
  getOrderFromStates,
  getVisibilityFromIds,
  getVisibilityFromStates,
} from "@/common/utils/datatable";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";

export type DataTableToolbarFunctionalityProps<TColumn extends string> =
  DataTableHasSearch &
    Pick<DataTableHasPagination, "canPaginate" | "totalCount"> &
    Pick<DataTableHasCustomization<TColumn>, "canCustomize">;

type Props<
  TData extends { id: string; subRows?: TData[] },
  TColumn extends string,
> = DataTableToolbarFunctionalityProps<TColumn> & {
  table: Table<TData>;
  defaultColumns: TColumn[];
  frozenColumnId?: string;
  hasPersistedState?: boolean;
  searchPlaceholder?: string;
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  columnCustomizerFooter?: ReactNode;
  isLoading?: boolean;
  _left?: FlexProps;
  _right?: FlexProps;
};

export const DataTableToolbar = <
  TData extends { id: string; subRows?: TData[] },
  TColumn extends string,
>({
  table,
  defaultColumns,
  frozenColumnId,
  hasPersistedState = false,
  searchPlaceholder = "Search",
  leftActions,
  rightActions,
  columnCustomizerFooter,
  isLoading,
  _left,
  _right,
  // Search
  canSearch,
  searchQuery,
  onSearchQueryChange,
  // Paginate
  canPaginate,
  totalCount,
  // Column States
  canCustomize,
}: Props<TData, TColumn>) => {
  const onDebouncedSearchQueryChange = useDebounceCallback(
    onSearchQueryChange ?? (() => {}),
    SEARCH_DEBOUNCE_MS,
  );

  if (
    !canSearch &&
    !canPaginate &&
    !canCustomize &&
    !leftActions &&
    !rightActions
  )
    return null;
  const columns = table.getAllFlatColumns();
  const columnOrder = table.getState().columnOrder;
  const columnVisible = table
    .getVisibleFlatColumns()
    .flatMap((c) => getId(c) ?? []);
  const { pageIndex, pageSize } = canPaginate
    ? table.getState().pagination
    : { pageIndex: 0, pageSize: 0 };

  return (
    <Flex
      alignItems="center"
      borderBottomColor="appBorder"
      borderBottomWidth={1}
      gap={2}
      justifyContent="space-between"
      px={6}
      py={3}
      w="full"
    >
      <Flex alignItems="center" gap={2} {..._left}>
        {canSearch && (
          <SearchInput
            defaultValue={searchQuery}
            placeholder={searchPlaceholder}
            onChange={(event) =>
              onDebouncedSearchQueryChange(event.currentTarget.value)
            }
          />
        )}
        {leftActions}
        {isLoading && <Spinner ml={2} size="xs" variant="subtle" />}
      </Flex>
      <Flex alignItems="center" gap={2} justifyContent="flex-end" {..._right}>
        {canPaginate && (
          <DataTablePagination
            isLoading={isLoading}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            {...table}
          />
        )}
        {rightActions}
        {canCustomize && (
          <DataTableCustomizer
            columnOrder={columnOrder}
            columns={columns}
            columnVisible={columnVisible}
            customFooter={columnCustomizerFooter}
            frozenColumnId={frozenColumnId}
            onColumnOrderChange={table.setColumnOrder}
            onColumnRemove={(id) => {
              table.setState((prevState) => ({
                ...prevState,
                columnOrder: prevState.columnOrder.filter((c) => c !== id),
                columnVisibility: omit(prevState.columnVisibility, id),
              }));
            }}
            onColumnReset={() => {
              const permanentColumns = columns.filter(
                ({ columnDef }) => !columnDef.meta?.isRemovable,
              );
              table.setState((prevState) => {
                return hasPersistedState
                  ? {
                      ...prevState,
                      columnOrder: getOrderFromStates(
                        permanentColumns,
                        [],
                        frozenColumnId,
                      ),
                      columnVisibility: getVisibilityFromStates(
                        permanentColumns,
                        [],
                        defaultColumns,
                        frozenColumnId,
                      ),
                    }
                  : {
                      ...prevState,
                      columnOrder: [],
                      columnVisibility: {},
                    };
              });
            }}
            onColumnVisibleChange={(visible) => {
              table.setColumnVisibility(getVisibilityFromIds(columns, visible));
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};
